import {
  Component,
  ElementRef,
  QueryList,
  ViewChild,
  ViewChildren,
  effect,
  inject,
  signal,
} from "@angular/core";
import { XwaiV2StreamService } from "./xwai-v2-stream.service";
import { AgentDetail } from "../xwai.types";
import { SharedModule } from "../../shared/shared.module";
import { MarkdownPipe } from "../../shared/pipes/markdown.pipe";
import { LineageTreePipe } from "./lineage-tree.pipe";
import { XwaiArtifactsComponent } from "../xwai-artifacts/xwai-artifacts.component";
import { XwaiKnowledgeComponent } from "../xwai-knowledge/xwai-knowledge.component";
import { XwaiTableComponent } from "../xwai-table/xwai-table.component";
import { XwaiTraceFitComponent } from "../xwai-trace-fit/xwai-trace-fit.component";
import { XwaiVizComponent } from "../xwai-viz/xwai-viz.component";

@Component({
  selector: "app-xwai-v2-stream",
  templateUrl: "./xwai-v2-stream.component.html",
  styleUrls: ["./xwai-v2-stream.component.less"],
  imports: [
    SharedModule,
    MarkdownPipe,
    LineageTreePipe,
    XwaiArtifactsComponent,
    XwaiKnowledgeComponent,
    XwaiTableComponent,
    XwaiTraceFitComponent,
    XwaiVizComponent,
  ],
})
export class XwaiV2StreamComponent {
  private service = inject(XwaiV2StreamService);

  @ViewChildren("messageEl") messageEls!: QueryList<ElementRef>;
  @ViewChild("scrollContainer") scrollContainer!: ElementRef<HTMLElement>;

  // Whether the user has scrolled up from the bottom of the messages list.
  readonly showScrollToBottom = signal(false);

  // Rotating status words shown while XWAI is working, before the first event.
  private readonly thinkingWords = [
    "Thinking",
    "Pondering",
    "Cogitating",
    "Reasoning",
    "Mulling it over",
    "Crunching",
    "Connecting the dots",
    "Digging in",
    "Untangling",
    "Working it out",
    "Synthesizing",
    "Noodling",
  ];
  readonly thinkingWord = signal(this.thinkingWords[0]);
  private thinkingTimer?: ReturnType<typeof setInterval>;

  // state surfaced from the service
  readonly messages = this.service.messages;
  readonly sending = this.service.sending;

  // live state for the in-flight streaming turn
  readonly streamAnswer = this.service.streamAnswer;
  readonly streamAgents = this.service.streamAgents;
  readonly streamSteps = this.service.streamSteps;

  constructor() {
    // Keep the conversation pinned to the bottom as content streams in.
    effect(() => {
      this.messages();
      this.streamAnswer();
      this.streamAgents();
      this.streamSteps();
      this.sending();
      setTimeout(() => this.scrollToBottom());
    });

    // Cycle through status words while XWAI is working.
    effect(() => {
      if (this.sending()) {
        let i = 0;
        this.thinkingWord.set(this.thinkingWords[0]);
        this.thinkingTimer = setInterval(() => {
          i = (i + 1) % this.thinkingWords.length;
          this.thinkingWord.set(this.thinkingWords[i]);
        }, 1800);
      } else if (this.thinkingTimer) {
        clearInterval(this.thinkingTimer);
        this.thinkingTimer = undefined;
      }
    });
  }

  ngOnDestroy() {
    if (this.thinkingTimer) clearInterval(this.thinkingTimer);
    this.service.cancel();
  }

  ngOnInit() {
    this.service.initSession();
  }

  ngAfterViewInit() {
    this.messageEls.changes.subscribe(() => this.scrollToBottom());
  }

  /** A finalized message's structured agent outputs, in order. */
  detailsOf(content: { details?: AgentDetail[] }): AgentDetail[] {
    return content.details ?? [];
  }

  private scrollToBottom() {
    const el = this.scrollContainer?.nativeElement;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }
  }

  /** Bound to (scroll) on the messages container; recomputes fab visibility. */
  onMessagesScroll() {
    const el = this.scrollContainer?.nativeElement;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    this.showScrollToBottom.set(distanceFromBottom > 1000);
  }

  /** "Scroll to bottom" fab click handler. */
  jumpToBottom() {
    this.scrollToBottom();
  }

  protected console_history() {
    const blob = new Blob([JSON.stringify(this.messages(), null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'conversation.json';
    a.click();
    URL.revokeObjectURL(url);
  }
}
